import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sparkles, ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, loginWithGoogle } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    
    const result = await register(email, password, fullName);
    
    if (result.success) {
      // Redirect to check-email page so user verifies before continuing
      navigate(`/check-email?email=${encodeURIComponent(email)}`);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div data-testid="register-page" className="min-h-screen bg-[#F7F5F2] dark:bg-background flex">
      {/* Left — Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white dark:bg-card">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2.5 mb-12 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/lumera-logo.png" alt="Lumera" className="w-10 h-10 rounded-full" />
            <span className="text-xl font-serif font-semibold text-foreground">Lumera</span>
          </div>

          <h1 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-2">Create your account</h1>
          <p className="text-muted-foreground font-sans mb-8">Start creating digital products in minutes.</p>

          {error && (
            <div data-testid="register-error" className="bg-red-50 border border-red-200 text-red-700 text-sm font-sans rounded-xl px-4 py-3 mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="fullName" className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">Full Name</Label>
              <Input
                data-testid="register-name-input"
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your name"
                className="bg-stone-50 border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl px-4 py-3 text-stone-800 placeholder:text-stone-400"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">Email</Label>
              <Input
                data-testid="register-email-input"
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="bg-stone-50 border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl px-4 py-3 text-stone-800 placeholder:text-stone-400"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">Password</Label>
              <Input
                data-testid="register-password-input"
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                minLength={8}
                className="bg-stone-50 border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl px-4 py-3 text-stone-800 placeholder:text-stone-400"
              />
              <p className="text-[10px] font-sans text-stone-400 mt-1">Minimum 8 characters</p>
            </div>
            <Button
              data-testid="register-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#B49A88] text-white hover:bg-[#B49A88]/85 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 font-sans font-semibold px-8 py-6 rounded-full text-base"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Create Account <ArrowRight className="ml-2 w-4 h-4" /></>}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-stone-200" /></div>
            <div className="relative flex justify-center"><span className="bg-white dark:bg-card px-3 text-xs text-stone-400 font-sans">or</span></div>
          </div>

          <Button
            data-testid="register-google-btn"
            type="button"
            onClick={loginWithGoogle}
            variant="outline"
            className="w-full flex items-center justify-center gap-3 border-stone-200 hover:bg-stone-50 rounded-full py-6 font-sans font-medium text-stone-700 text-base transition-all duration-300"
          >
            <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </Button>

          <p className="text-center text-sm font-sans text-muted-foreground mt-8">
            Already have an account?{' '}
            <Link to="/login" className="text-[#B49A88] font-semibold hover:underline">Log in</Link>
          </p>
        </div>
      </div>

      {/* Right — Benefits */}
      <div className="hidden lg:flex flex-1 bg-[#B49A88] dark:bg-[hsl(24,8%,16%)] items-center justify-center p-12">
        <div className="max-w-sm">
          <h2 className="text-2xl font-serif font-semibold text-white dark:text-[hsl(30,14%,90%)] mb-8">What you get with Create by Lumera</h2>
          <div className="space-y-5">
            {[
              '3 free product generations per month',
              '11 product types to choose from',
              'Professional PDF & PNG export',
              'AI-powered content generation',
              'No design skills required'
            ].map((benefit) => (
              <div key={benefit} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <p className="text-white/80 font-sans">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
